import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import { Truck, FileText, CreditCard } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const { cartItems, getCartTotal, clearCart, getEffectivePrice, customPrices } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [createAccount, setCreateAccount] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('pay_on_delivery');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.name?.split(' ')[0] || '',
    lastName: user?.name?.split(' ')[1] || '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    company: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (cartItems.length === 0) {
      navigate('/cart');
    }
  }, [cartItems.length, navigate]);

  if (cartItems.length === 0) {
    return null;
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Basic validation
    if (createAccount && formData.password !== formData.confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match",
        variant: "destructive"
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Prepare order data for API — apply contract pricing per item
      const orderItems = cartItems.map(item => ({
        product_id: item.id,
        name: item.name,
        price: getEffectivePrice(item),
        quantity: item.quantity
      }));
      const orderData = {
        items: orderItems,
        total: orderItems.reduce((s, i) => s + i.price * i.quantity, 0),
        shipping_info: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          company: formData.company || null
        },
        payment_method: paymentMethod,
        is_guest: !user && !createAccount
      };

      // Create order via API — include auth header if logged in for B2B detection
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/orders`, orderData, { headers });
      const createdOrder = response.data;

      // If credit card, redirect to Stripe checkout
      if (paymentMethod === 'credit_card') {
        try {
          const stripeRes = await axios.post(`${API}/checkout/create-session`, {
            order_id: createdOrder.order_id,
            origin_url: window.location.origin
          });
          // Redirect to Stripe hosted checkout
          window.location.href = stripeRes.data.url;
          return; // Don't proceed further
        } catch (stripeErr) {
          console.error('Stripe session creation failed:', stripeErr);
          toast({
            title: "Payment Error",
            description: "Could not initiate card payment. Your order has been saved — please try again or choose another payment method.",
            variant: "destructive"
          });
          setIsSubmitting(false);
          return;
        }
      }

      // If user wants to create an account
      if (createAccount && !user) {
        try {
          await axios.post(`${API}/auth/register`, {
            name: `${formData.firstName} ${formData.lastName}`,
            email: formData.email,
            password: formData.password,
            role: 'customer'
          });
          toast({
            title: "Account Created",
            description: "Your account has been created successfully!",
          });
        } catch (err) {
          console.error('Account creation failed:', err);
          // Don't block checkout if account creation fails
        }
      }

      clearCart();
      toast({
        title: "Order placed successfully!",
        description: `Order ${createdOrder.order_id} has been confirmed.`,
      });
      navigate('/order-confirmation', { state: { order: createdOrder } });
    } catch (error) {
      console.error('Order creation failed:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to place order. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const paymentMethods = [
    {
      id: 'pay_on_delivery',
      label: 'Pay on Delivery',
      description: 'Pay cash when your order is delivered',
      icon: Truck
    },
    {
      id: 'pay_by_invoice',
      label: 'Pay by Invoice',
      description: 'Receive an invoice and pay within 30 days',
      icon: FileText
    },
    {
      id: 'credit_card',
      label: 'Credit Card',
      description: 'Pay securely online via Stripe',
      icon: CreditCard
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 py-4 sm:py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-2xl sm:text-4xl font-bold mb-4 sm:mb-8 text-slate-800">Checkout</h1>

        {/* Guest Checkout Notice */}
        {!user && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
            <p className="text-purple-800 text-sm sm:text-base">
              <strong>Guest Checkout:</strong> You can complete your order without creating an account. 
              Optionally create an account below for faster checkout next time and to track your orders.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit}>
              {/* Shipping Information */}
              <Card className="mb-4 sm:mb-6 border-2">
                <CardHeader className="pb-2 sm:pb-4">
                  <CardTitle className="text-lg sm:text-xl">Shipping Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 sm:space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label htmlFor="firstName" className="text-sm">First Name *</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName" className="text-sm">Last Name *</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="company" className="text-sm">Company (Optional)</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="For invoice payments"
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label htmlFor="email" className="text-sm">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-sm">Phone *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="address" className="text-sm">Street Address *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                    <div className="col-span-2 sm:col-span-1">
                      <Label htmlFor="city" className="text-sm">City *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state" className="text-sm">State *</Label>
                      <Input
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="zipCode" className="text-sm">ZIP Code *</Label>
                      <Input
                        id="zipCode"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Create Account Option */}
              {!user && (
                <Card className="mb-4 sm:mb-6 border-2">
                  <CardContent className="pt-4 sm:pt-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <Checkbox
                        id="createAccount"
                        checked={createAccount}
                        onCheckedChange={setCreateAccount}
                      />
                      <Label htmlFor="createAccount" className="cursor-pointer text-sm sm:text-base">
                        Create an account for faster checkout and order tracking
                      </Label>
                    </div>
                    {createAccount && (
                      <div className="space-y-3 sm:space-y-4 pt-4 border-t">
                        <div>
                          <Label htmlFor="password" className="text-sm">Password *</Label>
                          <Input
                            id="password"
                            name="password"
                            type="password"
                            value={formData.password}
                            onChange={handleChange}
                            required={createAccount}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="confirmPassword" className="text-sm">Confirm Password *</Label>
                          <Input
                            id="confirmPassword"
                            name="confirmPassword"
                            type="password"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            required={createAccount}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Payment Method */}
              <Card className="mb-4 sm:mb-6 border-2">
                <CardHeader className="pb-2 sm:pb-4">
                  <CardTitle className="text-lg sm:text-xl">Payment Method</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {paymentMethods.map((method) => {
                      const Icon = method.icon;
                      return (
                        <label
                          key={method.id}
                          className={`flex items-start p-3 sm:p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            paymentMethod === method.id 
                              ? 'border-purple-500 bg-purple-50' 
                              : 'border-slate-200 hover:border-slate-300'
                          } ${method.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <input
                            type="radio"
                            name="payment"
                            value={method.id}
                            checked={paymentMethod === method.id}
                            onChange={(e) => !method.disabled && setPaymentMethod(e.target.value)}
                            disabled={method.disabled}
                            className="mt-1 w-4 h-4 text-purple-600"
                          />
                          <div className="ml-3 flex-1">
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
                              <span className="font-medium text-slate-800 text-sm sm:text-base">{method.label}</span>
                            </div>
                            <p className="text-xs sm:text-sm text-slate-500 mt-1">{method.description}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <Button 
                type="submit" 
                size="lg" 
                className="w-full bg-purple-600 hover:bg-purple-700 text-white text-base sm:text-lg py-3 sm:py-4"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Processing...' : 'Place Order'}
              </Button>
            </form>
          </div>

          {/* Order Summary */}
          <div>
            <Card className="border-2 lg:sticky lg:top-24">
              <CardHeader className="pb-2 sm:pb-4">
                <CardTitle className="text-lg sm:text-xl">Order Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                  {cartItems.map(item => (
                    <div key={item.id} className="flex justify-between text-xs sm:text-sm">
                      <span className="text-slate-600 flex-1 pr-2">
                        {item.name} x {item.quantity}
                      </span>
                      <span className="font-semibold text-slate-800">
                        ${(item.price * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="border-t pt-3 sm:pt-4 space-y-2">
                  <div className="flex justify-between text-slate-600 text-sm">
                    <span>Subtotal</span>
                    <span className="font-semibold">${getCartTotal().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600 text-sm">
                    <span>Shipping</span>
                    <span className="font-semibold text-green-600">FREE</span>
                  </div>
                  <div className="flex justify-between text-lg sm:text-xl font-bold text-slate-800 pt-2 border-t">
                    <span>Total</span>
                    <span className="text-purple-600">${getCartTotal().toFixed(2)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
